import Avatar from '@material-ui/core/Avatar';
import { LicenceId as AccountLicense } from '@ps/platform-message-framework/common/LicenceId';
import { Country, FeatureFlag } from '@ps/web-core-enums';
import { useIsMounted } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useContext, useEffect, useState } from 'react';

import { DataContext } from '@swf/components/StarsWizard/StarsWizard';
import useFeature from '@swf/core/hooks/useFeature';
import type { RootState } from '@swf/core/store';
import store, { select } from '@swf/core/store';

import * as fallbackLogo from '../../assets/logos/fallbackLogo.png';
import type { PaymentMethod } from '../../types/Cashier';
import { CardType } from '../../types/cardType';
import { getPaySystemName } from '../../utils/cashier';

import styles from './PaymentMethodLogo.scss';

interface Props extends PaymentMethod {
  variant?: 'result' | 'summary' | 'list' | 'small' | 'tiny' | 'payment';
  productId?: string;
}

export enum LogoRule {
  Country = 'country',
  License = 'license',
  LicenseCountry = 'license-country',
  LicenseDebitLogos = 'license-debit',
  Bank = 'bank'
}

interface LogoResult {
  src: string;
  extension: string;
}

const visaMasterCardRule = (
  license: AccountLicense,
  country: Country,
  isSwcMethodVerificationDeposit: boolean
) => {
  if (license === AccountLicense.SPAIN && isSwcMethodVerificationDeposit)
    return { name: [LogoRule.LicenseDebitLogos], extension: 'svg' };

  if (country === Country.Italy) return { name: [LogoRule.Country], extension: 'png' };

  if (license === AccountLicense.UK || license === AccountLicense.BELGIUM)
    return { name: [LogoRule.License], extension: 'svg' };

  if (
    (license === AccountLicense.DEFAULT || license === AccountLicense.EU) &&
    country === Country.Ireland
  )
    return { name: [LogoRule.LicenseCountry], extension: 'svg' };

  return { name: [LogoRule.Country], extension: 'svg' };
};

const rules = (
  cardType: CardType,
  license: AccountLicense,
  country: Country,
  isSwcMethodVerificationDeposit: boolean
) => {
  const rulesMap: Partial<Record<CardType, { name: LogoRule[]; extension: string }>> = {
    [CardType.EnvoyRtbt]: { name: [LogoRule.Country], extension: 'png' },
    [CardType.MasterCard]: visaMasterCardRule(license, country, isSwcMethodVerificationDeposit),
    [CardType.PaySafe]: { name: [LogoRule.Country], extension: 'png' },
    [CardType.Ricarica]: { name: [LogoRule.Country], extension: 'png' },
    [CardType.Rtbt]: { name: [LogoRule.Country], extension: 'png' },
    [CardType.Visa]: visaMasterCardRule(license, country, isSwcMethodVerificationDeposit),
    [CardType.WirecardWbt]: { name: [LogoRule.License], extension: 'png' },
    [CardType.OpenBanking]: { name: [LogoRule.Bank], extension: 'png' }
  };
  return rulesMap[cardType];
};

const svgLogos: CardType[] = [
  CardType.AstropayWallet,
  CardType.Bancontact,
  CardType.Bizum,
  CardType.DBT,
  CardType.ECheck,
  CardType.EcoPayZ,
  CardType.Eps,
  CardType.InstaDebit,
  CardType.InstantBank,
  CardType.Interac,
  CardType.LuxonPay,
  CardType.MasterCard,
  CardType.MBookers,
  CardType.MobilePay,
  CardType.MuchBetter,
  CardType.Multibanco,
  CardType.Neteller,
  CardType.Neteller2,
  CardType.OpenBanking,
  CardType.PayNearMe,
  CardType.PayPal,
  CardType.PaySafe,
  CardType.Payconiq,
  CardType.Pix,
  CardType.Resort,
  CardType.Todito,
  CardType.Visa,
  CardType.WebMoney,
  CardType.WireCashout,
  CardType.WirecardWbt,
  CardType.Klarna
];

const getKey = (object: Record<string, unknown>, value: AccountLicense): string | undefined => {
  // TODO: maybe convert to something like this? .map().filter((v) => v === value)
  for (const [k, v] of Object.entries(object)) {
    if (v === value) {
      return k;
    }
  }
};

const getLogoImage = async (filePath: string, extension: string) => {
  try {
    return (await import(`../../assets/logos/${filePath}.${extension}`)).default;
  } catch {
    return;
  }
};

const getFilePath = (cardType: CardType, rule?: LogoRule, value?: string, fileName?: string) =>
  `${rule && value ? value + '/' : ''}${(fileName || CardType[cardType])?.toLowerCase()}`;

const getRuleKeys = (
  cardType: CardType,
  state: RootState,
  isSwcMethodVerificationDeposit: boolean,
  productId?: string
): LogoResult[] => {
  const license = select.account.getLicense(state);
  const country = select.account.getCountry(state) as string;
  const cardRules = rules(cardType, license, country as Country, isSwcMethodVerificationDeposit);

  const ret: LogoResult[] = [];

  const defaultExtension = svgLogos.includes(cardType) ? 'svg' : 'png';

  if (!cardRules) return [];

  const { extension: ruleExtension } = cardRules;
  const ruleKeys = cardRules.name.map((rule: LogoRule) => {
    const licenseAbbr = getKey(AccountLicense, license);
    let value;

    switch (rule) {
      case LogoRule.LicenseCountry:
        value = `license/${licenseAbbr}/${country}`;
        break;
      case LogoRule.LicenseDebitLogos:
        value = `license/${licenseAbbr}/debit`;
        break;
      case LogoRule.Country:
        value = `country/${country}`;
        break;
      case LogoRule.License:
        value = `license/${licenseAbbr}`;
        break;
      case LogoRule.Bank:
        value = `bank`;
        break;
    }

    const extension = ruleExtension || defaultExtension;
    const src = getFilePath(cardType, rule, value, productId);
    return { src: src.toString().toLowerCase(), extension };
  });

  ret.push(...ruleKeys);

  return ret;
};

export const getLogoWithRules = async (
  cardType: CardType,
  isSwcMethodVerificationDeposit: boolean,
  productId?: string
): Promise<LogoResult> => {
  const ruleKeys = getRuleKeys(
    cardType,
    store.getState(),
    isSwcMethodVerificationDeposit,
    productId
  );

  const logos = await Promise.all(
    ruleKeys.map(({ src, extension }) =>
      getLogoImage(src, extension).then((src) => ({ src, extension }))
    )
  );

  const defaultExtension = svgLogos.find((e) => e === cardType) ? 'svg' : 'png';
  const defaultLogo = await getLogoImage(getFilePath(cardType), defaultExtension);

  const logo = logos.reduceRight((result, logo) => (logo.src ? logo : result), {
    src: defaultLogo,
    extension: defaultExtension
  });

  return logo;
};

const PaymentMethodLogo: React.FC<Props> = ({
  cardType,
  i18nSystemName = '',
  variant,
  productId
}) => {
  const [logo, setLogo] = useState<LogoResult>({ src: '', extension: 'png' });
  const [systemName, setSystemName] = useState('');
  const isMounted = useIsMounted();
  const isSwcMethodVerificationDeposit = useFeature(FeatureFlag.SwcMethodVerificationDeposit);

  const {
    data: { paySystems }
  } = useContext(DataContext);

  useEffect(() => {
    const applySrc = async () => {
      const logoResult = await getLogoWithRules(
        cardType,
        isSwcMethodVerificationDeposit,
        productId
      );
      if (isMounted()) {
        setLogo(logoResult);
      }
    };
    applySrc();

    return () => setLogo({ src: '', extension: 'png' });
  }, [cardType, isMounted, productId, isSwcMethodVerificationDeposit]);

  useEffect(() => {
    if (!i18nSystemName && paySystems) {
      const paySystemName = getPaySystemName(paySystems, cardType);
      setSystemName(paySystemName);
    } else {
      setSystemName(i18nSystemName);
    }
  }, [cardType, i18nSystemName, paySystems]);

  const logoSrc = logo?.src || fallbackLogo.default;
  const logoExtension = (logo?.src && logo?.extension) || 'png';
  let Svg = null;
  if (logoExtension === 'svg') {
    Svg = logoSrc;
  }

  return Svg ? (
    <div className={classNames(styles.root, variant && styles[variant as keyof typeof styles])}>
      <Svg />
    </div>
  ) : (
    <Avatar
      alt={systemName}
      src={logoSrc}
      className={classNames(!!variant && styles[variant as keyof typeof styles])}
      classes={{ root: styles.root }}
    >
      <img src={fallbackLogo.default} alt={systemName} className={styles.fallback} />
    </Avatar>
  );
};

export default PaymentMethodLogo;
